import { contentCatalog } from "../../content/catalog"
import { isLongGrassEntityTile, isTreeEntityTile } from "../../map/map"
import { tileSetSpecs } from "../base_map_builder"

/** The cell a generator writes to plant an entity tile: its composite PARENT. */
export interface PlantedTileSpec {
    tilesetId: string
    tilePos: number
}

const property = (tile: any, name: string) => tile?.properties?.find((p: any) => p.name === name)?.value

let parentsFor: unknown
let parents: Map<string, PlantedTileSpec>

/**
 * Where entity tile `id` is planted, read from the active catalog by its `entityTileId`.
 *
 * Looked up rather than written down because a Sprite Lab tile (the grove trees) lands in whatever
 * authored-terrain cell was free when it was saved, so its position is not a fact worth hardcoding.
 * Rebuilt whenever the catalog's tilesets object is replaced (a content release).
 */
export const entityTileParent = (id: string): PlantedTileSpec | undefined => {
    if (parentsFor !== contentCatalog.mapTilesets) {
        parents = new Map()
        for (const [tilesetId, sheet] of Object.entries(contentCatalog.mapTilesets)) {
            for (const tile of (sheet as any).tiles || []) {
                const entityTileId = property(tile, "entityTileId")
                if (entityTileId && !parents.has(entityTileId)) parents.set(entityTileId, { tilesetId, tilePos: tile.id })
            }
        }
        parentsFor = contentCatalog.mapTilesets
    }
    return parents.get(id)
}

let compositesFor: unknown
let composites: Map<string, PlantedTileSpec>

/**
 * Where SCENERY composite `id` is planted -- its `compositeTileIdParent` cell -- read from the active
 * catalog. Scenery (a tuft, a bush) has no entity tile to look it up by; like a Lab entity tile it
 * lands in whatever authored-terrain cell was free when it was saved.
 */
export const compositeTileParent = (id: string): PlantedTileSpec | undefined => {
    if (compositesFor !== contentCatalog.mapTilesets) {
        composites = new Map()
        for (const [tilesetId, sheet] of Object.entries(contentCatalog.mapTilesets)) {
            for (const tile of (sheet as any).tiles || []) {
                const composite = property(tile, "compositeTileIdParent")
                if (composite && !composites.has(composite)) composites.set(composite, { tilesetId, tilePos: tile.id })
            }
        }
        compositesFor = contentCatalog.mapTilesets
    }
    return composites.get(id)
}

/** The tileset and position a room gid points at, by the generators' fixed firstgids (tileSetSpecs). */
export const tileSpecAtGid = (gid: number): PlantedTileSpec | undefined => {
    let best: { tilesetId: string; firstgid: number } | undefined
    for (const spec of Object.values(tileSetSpecs)) {
        if (spec.firstgid <= gid && (!best || spec.firstgid > best.firstgid)) best = spec
    }
    return best && { tilesetId: best.tilesetId, tilePos: gid - best.firstgid }
}

let gidTilesFor: unknown
let gidTiles: Map<number, string | undefined>

/** The entity tile a gid on a generated room's entity layer plants, if it plants one. */
export const entityTileAtGid = (gid: number): string | undefined => {
    if (!(gid > 0)) return undefined
    if (gidTilesFor !== contentCatalog.mapTilesets) {
        gidTiles = new Map()
        gidTilesFor = contentCatalog.mapTilesets
    }
    if (!gidTiles.has(gid)) {
        const spec = tileSpecAtGid(gid)
        const sheet = spec && contentCatalog.mapTilesets[spec.tilesetId]
        const tile = (sheet as any)?.tiles?.find((t: any) => t.id === spec.tilePos)
        gidTiles.set(gid, property(tile, "entityTileId"))
    }
    return gidTiles.get(gid)
}

/** True when a gid on a generated room's entity layer plants a grown tree (its `tree` tag, not its id). */
export const isTreeGid = (gid: number): boolean => isTreeEntityTile(entityTileAtGid(gid))

/** True when a gid on a generated room's entity layer plants long grass (its `grass` tag, not its id). */
export const isLongGrassGid = (gid: number): boolean => isLongGrassEntityTile(entityTileAtGid(gid))
